import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Clock3,
  Copy,
  Loader2,
  Upload,
  CheckCircle2,
  Download,
  Mail,
  MessageCircle,
  User,
  FileText,
  X,
  LayoutDashboard,
} from "lucide-react";

const TRANSACTION_ID_REGEX = /^[A-Za-z0-9\-_.\/ ]{4,100}$/;

import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { getAvatarSrc } from "../lib/avatar";
import { formatDate } from "../lib/dateFormat";
import { getPaymentProofSrc } from "../lib/paymentProof";

const PAYMENT_QR_FALLBACK = "/gpay-qr.jpeg";

export default function MockCheckout() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const proofInputRef = useRef(null);
  const [member, setMember] = useState(loc.state?.member || null);
  const [meta, setMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [avatarError, setAvatarError] = useState(false);
  const savedProofSrc = getPaymentProofSrc(member);

  useEffect(() => {
    if (member) return;
    api
      .get(`/membership/${id}`)
      .then(({ data }) => setMember(data))
      .catch((e) => {
        toast.error(formatApiError(e));
        nav("/membership");
      });
  }, [id, member, nav]);

  useEffect(() => {
    api
      .get("/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (proofPreview) URL.revokeObjectURL(proofPreview);
    };
  }, [proofPreview]);

  const paymentQr = meta?.payment_qr_url || PAYMENT_QR_FALLBACK;
  const paymentUpi = meta?.payment_upi_id || "";
  const resumeLink = `${window.location.origin}/checkout/${id}`;
  const shareText = encodeURIComponent(
    `Namaste,\n\nPlease find my saved membership application link below:\n\n${resumeLink}\n\nApplication ID: ${id}\n\nPlease use this link to resume payment and continue the application.\n\nRegards,\n${member?.name || "Member"}`,
  );
  const emailSubject = encodeURIComponent("OSPF membership application resume link");
  const emailBody = encodeURIComponent(
    `Namaste,\n\nPlease find my saved membership application link below:\n\n${resumeLink}\n\nApplication ID: ${id}\n\nPlease use this link to resume payment and continue the application.\n\nRegards,\n${member?.name || "Member"}`,
  );
  const whatsappLink = `https://wa.me/?text=${shareText}`;
  const resumeEmailLink = `mailto:?subject=${emailSubject}&body=${emailBody}`;
  const dueAtLabel = useMemo(() => {
    if (!member?.payment_due_at) return "";
    const due = new Date(member.payment_due_at);
    if (Number.isNaN(due.getTime())) return member.payment_due_at;
    return formatDate(due);
  }, [member?.payment_due_at]);

  const amount =
    Number(member?.amount) > 0
      ? member.amount
      : member?.residence_type === "indian"
        ? 2100
        : 1100;

  const onProofFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProofFile(file);
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofPreview(URL.createObjectURL(file));
  };

  const clearProof = () => {
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(null);
    setProofPreview("");
    if (proofInputRef.current) proofInputRef.current.value = "";
  };

  const copyUpi = async () => {
    if (!paymentUpi) {
      toast.error("UPI ID is not configured.");
      return;
    }
    try {
      await navigator.clipboard.writeText(paymentUpi);
      toast.success("UPI ID copied.");
    } catch {
      toast.error("Could not copy UPI ID.");
    }
  };

  const submitProof = async () => {
    const txValue = transactionId.trim();
    if (!txValue) {
      toast.error("Please enter transaction ID.");
      return;
    }
    if (!TRANSACTION_ID_REGEX.test(txValue)) {
      toast.error(
        "Please enter a valid transaction ID (4-100 letters, numbers, - _ . / or spaces).",
      );
      return;
    }
    if (!proofFile) {
      toast.error("Please upload payment proof screenshot.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("member_id", id);
      fd.append("transaction_id", txValue);
      fd.append("proof", proofFile);
      const { data } = await api.post("/membership/payment-proof", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      nav(`/success/${id}`, {
        state: {
          member: data.member,
          proofPreview,
        },
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!member || !meta) {
    return (
      <div className="min-h-screen bg-[#02042B] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const planLabel =
    member.residence_type === "indian" ? "Indian Resident" : "NRI / Overseas";

  return (
    <div
      className="min-h-screen bg-cream-deep flex flex-col"
      data-testid="mock-checkout-page"
    >
      <header className="bg-[#02042B] text-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-heading text-base sm:text-lg font-semibold truncate">
              Payment verification
            </div>
            <div className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-white/60">
              QR payment · Proof upload
            </div>
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs text-white/80 rounded-full bg-white/10 px-2.5 sm:px-3 py-1 sm:py-1.5 shrink-0">
            <Clock3 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">3-day payment window</span>
            <span className="sm:hidden">3-day window</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* ---- LEFT: Application Summary ---- */}
          <aside className="lg:col-span-5 xl:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-3xl border border-black/5 bg-white p-5 sm:p-6 shadow-premium">
                <div className="overline mb-4">Application summary</div>

                <div className="mb-5 rounded-2xl overflow-hidden border border-black/5 bg-cream-deep aspect-[4/3] flex items-center justify-center">
                  {getAvatarSrc(member) && !avatarError ? (
                    <img
                      src={getAvatarSrc(member)}
                      alt={member.name}
                      onError={() => setAvatarError(true)}
                      className="w-full h-full object-contain bg-white"
                      data-testid="checkout-photo-preview"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-ink-muted">
                      <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center">
                        <User className="w-8 h-8 text-brand" />
                      </div>
                      <div className="font-heading text-lg text-ink">
                        {member.name}
                      </div>
                    </div>
                  )}
                </div>

                <div className="divide-y divide-black/5 text-sm">
                  <Row k="Member" v={member.name} />
                  <Row k="Email" v={member.email} mono />
                  <Row k="Plan" v={planLabel} />
                  <Row k="Amount" v={`₹${amount}`} bold />
                  <Row
                    k="Payment status"
                    v={
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-medium">
                        <Clock3 className="w-3 h-3" />
                        {member.payment_status}
                      </span>
                    }
                  />
                  <Row k="Due by" v={dueAtLabel || "—"} />
                </div>

                <div className="mt-5 flex gap-3 rounded-2xl bg-cream-deep border border-black/5 p-4 text-sm text-ink-soft">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <p>
                    Save this application and complete payment within 3 days.
                    After uploading proof and transaction ID, click final
                    submit.
                  </p>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="btn-secondary w-full"
                    onClick={() => nav(`/membership/print/${id}`, { state: { member } })}
                  >
                    Print application PDF
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* ---- RIGHT: Payment + Proof ---- */}
          <section className="lg:col-span-7 xl:col-span-8 order-1 lg:order-2 space-y-6">
            {/* Payment Method Toggle */}
            <div className="rounded-3xl border border-black/5 bg-white p-2 shadow-premium">
              <div className="grid grid-cols-2 gap-1 text-sm">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("upi")}
                  className={`rounded-2xl py-2.5 font-medium transition ${
                    paymentMethod === "upi"
                      ? "bg-[#02042B] text-white shadow"
                      : "text-ink-soft hover:bg-cream-deep"
                  }`}
                >
                  UPI (Scan QR)
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("cash")}
                  className={`rounded-2xl py-2.5 font-medium transition ${
                    paymentMethod === "cash"
                      ? "bg-[#02042B] text-white shadow"
                      : "text-ink-soft hover:bg-cream-deep"
                  }`}
                >
                  Cash Payment
                </button>
              </div>
            </div>

            {/* Pay by Scan / QR */}
            <div className="rounded-3xl bg-[#0B1020] text-white p-5 sm:p-7 lg:p-8 shadow-premium relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
                <div>
                  <div className="overline text-white/65 mb-3">Pay by scan</div>
                  <h1 className="font-heading text-2xl sm:text-3xl md:text-[2rem] font-light leading-tight">
                    {paymentMethod === "upi"
                      ? "Scan the QR with any UPI app"
                      : "Pay in cash at the office"}
                  </h1>
                  <p className="mt-3 text-white/70 leading-relaxed text-sm sm:text-base">
                    Your form is already saved. Please pay the membership
                    amount, take a screenshot of the payment, and upload the
                    proof with the transaction ID.
                  </p>

                  <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-4">
                    <div className="text-xs text-white/55 uppercase tracking-wider mb-1">
                      Amount to pay
                    </div>
                    <div className="font-heading text-3xl sm:text-4xl">
                      ₹{amount}
                    </div>
                    {paymentMethod === "upi" && paymentUpi && (
                      <div className="mt-3 text-sm text-white/65">
                        UPI ID:{" "}
                        <span className="font-medium text-white break-all">
                          {paymentUpi}
                        </span>
                      </div>
                    )}
                  </div>

                </div>

                {/* QR Card */}
                <div className="flex justify-center md:justify-end">
                  {paymentMethod === "upi" ? (
                    <div className="w-full max-w-[280px]">
                      <div className="rounded-2xl bg-white p-3 sm:p-4 shadow-premium">
                        <div
                          role="img"
                          aria-label="Payment QR"
                          className="w-full aspect-square rounded-xl bg-white"
                          style={{
                            backgroundImage: `url(${paymentQr})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "auto 164%",
                            backgroundPosition: "center 42%",
                          }}
                        />
                        <div className="mt-2 text-center text-xs text-ink-muted">
                          Scan with Google Pay, PhonePe, Paytm
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={copyUpi}
                          disabled={!paymentUpi}
                          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-50 px-3 py-2 text-xs sm:text-sm font-medium text-white transition"
                        >
                          <Copy className="w-4 h-4" />
                          Copy UPI
                        </button>
                        <a
                          href={paymentQr}
                          download
                          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover px-3 py-2 text-xs sm:text-sm font-medium text-white transition"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-white/70 w-full max-w-[280px]">
                      <FileText className="w-10 h-10 mx-auto mb-3 text-white/50" />
                      <div className="font-medium text-white">
                        Cash payment selected
                      </div>
                      <p className="text-sm mt-2">
                        Pay at the office and upload the receipt below.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Proof Upload + Transaction ID */}
            <div className="rounded-3xl border border-black/5 bg-white p-5 sm:p-7 shadow-premium">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="overline mb-1">Step 2</div>
                  <h2 className="font-heading text-xl sm:text-2xl text-ink">
                    Upload proof &amp; submit
                  </h2>
                </div>
              </div>

              <label className="block text-sm font-medium text-ink-soft mb-2">
                Transaction / Reference ID
              </label>
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="e.g. UPI ref no. or receipt number"
                className="input-premium mb-5"
                data-testid="transaction-id-input"
              />

              <label className="block text-sm font-medium text-ink-soft mb-2">
                {paymentMethod === "upi"
                  ? "Payment proof screenshot"
                  : "Cash receipt proof"}
              </label>

              <input
                ref={proofInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={onProofFile}
              />

              {!proofFile ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => proofInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      proofInputRef.current?.click();
                    }
                  }}
                  className="w-full cursor-pointer rounded-2xl border-2 border-dashed border-brand/40 bg-brand/5 p-6 sm:p-8 text-center hover:bg-brand/10 hover:border-brand transition"
                >
                  <Upload className="w-7 h-7 mx-auto mb-2 text-brand" />
                  <div className="font-medium text-brand">
                    Tap to upload screenshot or PDF
                  </div>
                  <p className="text-xs text-ink-muted mt-1">
                    JPEG, PNG or PDF · up to 5 MB
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-black/5 bg-cream-deep overflow-hidden">
                  <div className="bg-black/5">
                    {proofFile.type === "application/pdf" ? (
                      <object
                        data={proofPreview}
                        type="application/pdf"
                        className="w-full h-[360px]"
                        aria-label="Payment proof preview"
                        data-testid="proof-preview-pdf"
                      >
                        <div className="p-5 flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                            <FileText className="w-6 h-6 text-brand" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-ink truncate">
                              {proofFile.name}
                            </div>
                            <div className="text-xs text-ink-muted">
                              PDF uploaded. Open in browser or submit as-is.
                            </div>
                          </div>
                        </div>
                      </object>
                    ) : (
                      <img
                        src={proofPreview}
                        alt="Payment proof preview"
                        className="w-full max-h-[360px] object-contain"
                        data-testid="proof-preview-image"
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-t border-black/5">
                    <span className="text-xs text-ink-muted truncate">
                      {proofFile.name}
                    </span>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => proofInputRef.current?.click()}
                        className="text-xs font-medium text-brand hover:underline"
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={clearProof}
                        className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-red-600"
                      >
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

                <div className="mt-4 rounded-2xl border border-black/5 bg-cream-deep p-4 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-ink-muted">Transaction ID</span>
                  <span className="font-medium text-ink break-all">
                    {transactionId.trim() || "Not entered yet"}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-ink-muted">Proof preview</span>
                  <span className="font-medium text-ink break-all">
                    {proofFile?.name || (savedProofSrc ? "Saved proof available" : "Not uploaded yet")}
                  </span>
                </div>
                {savedProofSrc && !proofFile ? (
                  <div className="mt-3 rounded-xl overflow-hidden border border-black/5 bg-white">
                    <img
                      src={savedProofSrc}
                      alt="Saved payment proof"
                      className="w-full max-h-[240px] object-contain"
                      data-testid="saved-proof-preview"
                    />
                  </div>
                ) : null}
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(resumeLink);
                      toast.success("Resume link copied");
                    } catch {
                      toast.error("Could not copy resume link");
                    }
                  }}
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy resume link
                </button>
                <a
                  href={resumeEmailLink}
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Share by email
                </a>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Share on WhatsApp
                </a>
              </div>

              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-xs sm:text-sm text-ink-soft">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                  Your form data stays saved while you complete payment.
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => nav("/member/dashboard")}
                    className="btn-secondary px-6 py-2.5 text-sm w-full sm:w-auto justify-center"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Go to dashboard
                  </button>
                  <button
                    onClick={submitProof}
                    disabled={submitting}
                    className="btn-primary px-8 py-2.5 text-sm w-full sm:w-auto"
                    data-testid="final-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>Final submit</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function Row({ k, v, mono = false, bold = false }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-ink-muted text-xs uppercase tracking-wider">
        {k}
      </span>
      <span
        className={`text-ink text-right ${bold ? "font-heading text-base font-semibold" : "font-medium text-sm"} ${mono ? "break-all" : ""}`}
      >
        {v}
      </span>
    </div>
  );
}
